import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import FinanceDashboardPage from './FinanceDashboardPage';
import BGDataFinalListDashboardPage from './BGDataFinalListDashboardPage';
import CashEmdDashboardPage from './CashEmdDashboardPage';
import ClaimMisDashboardPage from './ClaimMisDashboardPage';
import InsuranceMisDashboardPage from './InsuranceMisDashboardPage';
import OMAttendanceDashboardPage from './OMAttendanceDashboardPage';
import NfsFrtTechnicianDashboardPage from './NfsFrtTechnicianDashboardPage';
import SCMDashboardPage from './SCMDashboardPage';
import HRLeadershipTMDashboardPage from './HRLeadershipTMDashboardPage';
import HRLearningDevelopmentDashboardPage from './HRLearningDevelopmentDashboardPage';
import HRDigitalEnablementDashboardPage from './HRDigitalEnablementDashboardPage';
import HRMainDashboardPage from './HRMainDashboardPage.js';
import ITAssetManagementPage from './ITAssetManagementPage';

import DepartmentPlaceholderPage from './DepartmentPlaceholderPage';


import { departmentAPI } from '../utils/api';
import { getUser, saveUser } from '../utils/auth';
import { userAPI } from '../utils/api';
import { normalizeAlphaNumLower } from '../utils/string';
import { FINANCE_PROJECT_NAME_TO_ID } from '../utils/dashboardProjects';
import { isOmDepartmentKey, isHrDepartmentKey } from '../utils/departmentKeys';

const EXCO_ALL_DASHBOARDS = '__ALL__';

const normalizeKey = (value) => normalizeAlphaNumLower(value);

// Derived from the canonical Finance project list (../utils/dashboardProjects)
// so a display-name edit there can never silently break this lookup again,
// plus legacy/alternate name variants seen in real DB-seeded project rows
// that aren't the current display name but should still resolve to the
// same page.
const FINANCE_PROJECT_ALIAS_TO_ID = {
  ...FINANCE_PROJECT_NAME_TO_ID,
  bgdatafinallist: 'finance-bg-data-final-list',
  cashemds: 'finance-cash-emd-list',
  claimmislist: 'finance-claim-mis-list',
  insurancemislist: 'finance-insurance-mis-list'
};

// Adds a normalized-key alias for every entry so a lookup that normalizes its
// input (strips dashes/case) still finds the same component. Without this,
// normalizeKey('finance-bg-data-final-list') -> 'financebgdatafinallist'
// could never match the map's own dashed keys — the id-based lookup path
// was silently dead code, saved only by the name-based alias path picking
// up the slack.
const withNormalizedAliases = (componentMap) => {
  const merged = { ...componentMap };
  Object.entries(componentMap).forEach(([key, Component]) => {
    const normalized = normalizeKey(key);
    if (!(normalized in merged)) merged[normalized] = Component;
  });
  return merged;
};

const FINANCE_COMPONENT_BY_PROJECT_ID = withNormalizedAliases({
  'finance-bg-data-final-list': BGDataFinalListDashboardPage,
  'finance-cash-emd-list': CashEmdDashboardPage,
  'finance-claim-mis-list': ClaimMisDashboardPage,
  'finance-insurance-mis-list': InsuranceMisDashboardPage
});

const OM_COMPONENT_BY_PROJECT_ID = withNormalizedAliases({
  'om-nfs-frt-technician-dashboard': NfsFrtTechnicianDashboardPage,
  'om-attendance-dashboard': OMAttendanceDashboardPage
});

const HR_COMPONENT_BY_PROJECT_ID = withNormalizedAliases({
  'hr-main-dashboard': HRMainDashboardPage,
  'hr-leadership-tm': HRLeadershipTMDashboardPage,
  'hr-learning-development': HRLearningDevelopmentDashboardPage,
  'hr-digital-enablement': HRDigitalEnablementDashboardPage
});

// Name-fallback keys must match the real seeded project names
// (backend/src/services/itAssetsProjectSeed.js) normalized — 'Overall Asset
// Master' -> 'overallassetmaster', 'Active Asset' -> 'activeasset'. This map
// previously had 'masterdata'/'assetmaster'/'currentallocation' instead,
// which never matched any real name; only projectCode ever resolved these,
// masking the gap.
const IT_TAB_BY_PROJECT_KEY = {
  itassetdash: 'dashboard',
  itassetmy: 'my',
  itassetmstr: 'master',
  itassetaloc: 'inventory',
  itassethist: 'history',
  itassetusrhist: 'user_history',
  itassetaudt: 'audit',
  dashboard: 'dashboard',
  myassets: 'my',
  overallassetmaster: 'master',
  activeasset: 'inventory',
  assethistory: 'history',
  userhistory: 'user_history',
  assetaudit: 'audit'
};

const resolveProjectComponent = ({ componentMap, selectedProjectId }) => {
  const rawId = String(selectedProjectId || '').trim();
  if (rawId && componentMap[rawId]) return componentMap[rawId];
  const normalizedId = normalizeKey(rawId);
  return normalizedId ? componentMap[normalizedId] : null;
};

const resolveFinanceProjectId = (selectedProject) => {
  const idKey = normalizeKey(selectedProject?.id);
  if (FINANCE_COMPONENT_BY_PROJECT_ID[idKey]) return idKey;
  const nameKey = normalizeKey(selectedProject?.name);
  return FINANCE_PROJECT_ALIAS_TO_ID[nameKey] || null;
};

const getExcoDashboardIdsForDepartment = (user, departmentId) => {
  if (user?.roleType !== 'EXCO') return [];
  const department = getUser()?.departments?.find(d => Number(d.id) === Number(departmentId));
  const dashboardAccess = user?.excoDashboardAccess || {};
  
  // Try matching by ID first
  let explicitIds = dashboardAccess?.[departmentId] || dashboardAccess?.[String(departmentId)];

  // Fallback: If no match by ID, check by normalized name to handle environment ID shifts
  if (!explicitIds && department?.name) {
    const normalizedName = normalizeAlphaNumLower(department.name);
    explicitIds = dashboardAccess?.[normalizedName];
  }

  if (Array.isArray(explicitIds) && explicitIds.length > 0) {
    return explicitIds;
  }

  const assignedDepartmentIds = Array.isArray(user?.departmentIds)
    ? user.departmentIds.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0)
    : [];

  return assignedDepartmentIds.includes(Number(departmentId)) ? [EXCO_ALL_DASHBOARDS] : [];
};

const DepartmentDashboardsPage = () => {
  const [departments, setDepartments] = useState([]);
  const [activeDepartmentId, setActiveDepartmentId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(getUser());

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await departmentAPI.getAllDepartments();
        const scopedDepartments = Array.isArray(response?.data) ? response.data : [];
        const savedDeptRaw = localStorage.getItem('selectedDept');
        const savedDept = savedDeptRaw ? JSON.parse(savedDeptRaw) : null;
        const matchedSavedDept = savedDept
          ? scopedDepartments.find((dept) => Number(dept.id) === Number(savedDept.id))
          : null;
        const savedProjectRaw = localStorage.getItem('selectedProject');
        const savedProject = savedProjectRaw ? JSON.parse(savedProjectRaw) : null;

        setDepartments(scopedDepartments);
        // Only restore saved dept if it still exists — clear stale localStorage otherwise
        if (matchedSavedDept) {
          setActiveDepartmentId(matchedSavedDept.id);
        } else {
          setActiveDepartmentId(scopedDepartments[0]?.id || null);
          // Clear stale dept/project from localStorage so it doesn't cause wrong API calls
          if (savedDept && !matchedSavedDept) {
            localStorage.removeItem('selectedDept');
            localStorage.removeItem('selectedProject');
          }
        }
        setSelectedProject(savedProject || null);
      } catch (err) {
        setError('Failed to load department dashboards.');
      } finally {
        setLoading(false);
      }
    };

    fetchDepartments();
  }, []);

  useEffect(() => {
    const syncDepartmentFromNavbar = () => {
      const savedDeptRaw = localStorage.getItem('selectedDept');
      const savedDept = savedDeptRaw ? JSON.parse(savedDeptRaw) : null;
      if (!savedDept?.id) return;

      const isAccessible = departments.some((dept) => Number(dept.id) === Number(savedDept.id));
      if (isAccessible) {
        setActiveDepartmentId(savedDept.id);
      }
    };

    globalThis.addEventListener('departmentChanged', syncDepartmentFromNavbar);
    return () => globalThis.removeEventListener('departmentChanged', syncDepartmentFromNavbar);
  }, [departments]);

  useEffect(() => {
    const syncProjectFromNavbar = () => {
      const savedProjectRaw = localStorage.getItem('selectedProject');
      const savedProject = savedProjectRaw ? JSON.parse(savedProjectRaw) : null;
      setSelectedProject(savedProject || null);
    };

    globalThis.addEventListener('projectChanged', syncProjectFromNavbar);
    return () => globalThis.removeEventListener('projectChanged', syncProjectFromNavbar);
  }, []);

  const activeDepartment = useMemo(
    () => departments.find((dept) => Number(dept.id) === Number(activeDepartmentId)) || null,
    [departments, activeDepartmentId]
  );
  useEffect(() => {
    const syncCurrentUserAccess = async () => {
      const loggedInUser = getUser();
      const userId = Number(loggedInUser?.id);
      if (!Number.isInteger(userId) || userId <= 0) return;

      try {
        const response = await userAPI.getUserById(userId);
        if (!response?.success || !response?.data) return;

        const mergedUser = {
          ...loggedInUser,
          ...response.data,
          departmentIds: Array.isArray(response.data.departmentIds) ? response.data.departmentIds : (loggedInUser?.departmentIds || []),
          excoDashboardAccess: response.data.excoDashboardAccess || loggedInUser?.excoDashboardAccess || {}
        };

        saveUser(mergedUser);
        setCurrentUser(mergedUser);
      } catch (err) {
        if (err?.statusCode === 403) return; // user doesn't have users:read permission
        console.error('Failed to refresh current user access:', err);
      }
    };

    syncCurrentUserAccess();

    globalThis.addEventListener('focus', syncCurrentUserAccess);
    return () => globalThis.removeEventListener('focus', syncCurrentUserAccess);
  }, []);

  useEffect(() => {
    const roleType = String(currentUser?.roleType || currentUser?.role?.roleType || '').toUpperCase();
    if (!activeDepartment || !['EXCO', 'IT_HEAD'].includes(roleType) || !selectedProject?.id) return;

    // IT & Automation: EXCO should only ever access "My Assets"
    const deptKey = normalizeKey(activeDepartment?.name);
    if (deptKey === 'itautomation' || deptKey === 'itandautomation' || deptKey === 'it' || deptKey === 'itautomationdepartment') {
      const projectCodeKey = normalizeKey(selectedProject?.projectCode);
      const projectNameKey = normalizeKey(selectedProject?.name);
      const isMyAssets = projectCodeKey === 'itassetmy' || projectNameKey === 'myassets';
      if (roleType === 'EXCO' && !isMyAssets) {
        setSelectedProject(null);
        localStorage.removeItem('selectedProject');
        globalThis.dispatchEvent(new Event('projectChanged'));
      }
      return;
    }

    if (roleType === 'IT_HEAD') return;

    const allowedDashboardIds = getExcoDashboardIdsForDepartment(currentUser, activeDepartment.id);
    if (allowedDashboardIds.includes(EXCO_ALL_DASHBOARDS)) return;
    if (!allowedDashboardIds.length) {
      setSelectedProject(null);
      localStorage.removeItem('selectedProject');
      globalThis.dispatchEvent(new Event('projectChanged'));
      return;
    }

    const selectedProjectId = String(selectedProject.id);
    if (!allowedDashboardIds.includes(selectedProjectId)) {
      setSelectedProject(null);
      localStorage.removeItem('selectedProject');
      globalThis.dispatchEvent(new Event('projectChanged'));
    }
  }, [activeDepartment, currentUser, selectedProject]);

  const renderDepartmentDashboard = () => {
    if (!activeDepartment) return null;

    const departmentKey = normalizeKey(activeDepartment.name);

    if (departmentKey === 'finance') {
      const financeProjectId = resolveFinanceProjectId(selectedProject);
      const SelectedComponent = financeProjectId ? FINANCE_COMPONENT_BY_PROJECT_ID[financeProjectId] : null;
      if (SelectedComponent) return <SelectedComponent departmentId={activeDepartment.id} />;
      return <FinanceDashboardPage departmentId={activeDepartment.id} departmentName={activeDepartment.name} />;
    }

    if (isOmDepartmentKey(departmentKey)) {
      const SelectedComponent = resolveProjectComponent({
        componentMap: OM_COMPONENT_BY_PROJECT_ID,
        selectedProjectId: selectedProject?.id
      }) || NfsFrtTechnicianDashboardPage;
      return <SelectedComponent departmentId={activeDepartment.id} />;
    }

    if (departmentKey === 'scm' || departmentKey === 'supplychain' || departmentKey === 'supplychainmanagement') {
      return (
        <SCMDashboardPage
          departmentId={activeDepartment.id}
          departmentName={activeDepartment.name}
        />
      );
    }

    if (isHrDepartmentKey(departmentKey)) {
      const SelectedComponent = resolveProjectComponent({
        componentMap: HR_COMPONENT_BY_PROJECT_ID,
        selectedProjectId: selectedProject?.id || 'hr-main-dashboard'
      }) || DepartmentPlaceholderPage;
      if (SelectedComponent === DepartmentPlaceholderPage) return <DepartmentPlaceholderPage departmentName={activeDepartment.name} />;
      return <SelectedComponent departmentId={activeDepartment.id} />;
    }

    if (departmentKey === 'itautomation' || departmentKey === 'itandautomation' || departmentKey === 'it' || departmentKey === 'itautomationdepartment') {
      const projectCodeKey = normalizeKey(selectedProject?.projectCode);
      const projectNameKey = normalizeKey(selectedProject?.name);
      const tab = IT_TAB_BY_PROJECT_KEY[projectCodeKey] || IT_TAB_BY_PROJECT_KEY[projectNameKey] || 'my';
      return <ITAssetManagementPage initialTab={tab} />;
    }

    return <DepartmentPlaceholderPage departmentName={activeDepartment.name} />;
  };


  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0">Loading department dashboards...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!departments.length) {
    return (
      <Alert variant="warning">
        No departments are assigned to your account yet.
      </Alert>
    );
  }

  return (
    <>
      {renderDepartmentDashboard()}
    </>
  );
};

export default DepartmentDashboardsPage;
